// Tableau de bord (`Docs/Fondations/V1/PROPOSITION_TABLEAU_DE_BORD.md`): the server decides which blocks a person gets (module +
// permission, each list's own scope); this file only turns their figures into words and links. A figure with a list behind it
// opens that list, filtered through router state the list consumes once (Tasks.jsx: `filters`).

// « À traiter »: one line per kind of thing waiting for the person; a line at zero is not shown.
export const TODO_LINES = {
  my_tasks_late_or_today: {
    one: "tâche en retard ou à faire aujourd'hui", many: "tâches en retard ou à faire aujourd'hui",
    to: "/tasks", state: { filters: { due: "overdue", responsable: "moi" } },
  },
  my_meetings_today: {
    one: "rendez-vous aujourd'hui", many: "rendez-vous aujourd'hui",
    to: "/tasks", state: { filters: { due: "today", type: "rdv", responsable: "moi" } },
  },
  my_missions_today: { one: "mission aujourd'hui", many: "missions aujourd'hui", to: "/missions?tab=planning" },
  reports_to_write: { one: "compte rendu de mission à rédiger", many: "comptes rendus de mission à rédiger", to: "/missions?tab=mine" },
  reports_to_review: { one: "compte rendu de mission à valider", many: "comptes rendus de mission à valider", to: "/missions?tab=review" },
  critical_issues: {
    one: "anomalie matériel critique ouverte", many: "anomalies matériel critiques ouvertes", to: "/equipements?gravite=critical",
  },
  my_pilots_at_risk: {
    one: "de mes pilotes à surveiller ou bloqué", many: "de mes pilotes à surveiller ou bloqués",
    to: "/pilotes", state: { filters: { responsible: "me" } },
  },
  claims_to_review: { one: "note de frais à approuver", many: "notes de frais à approuver", to: "/finance?tab=review" },
  assist_waiting: { one: "demande d'assistance en attente", many: "demandes d'assistance en attente", to: "/assistance" },
  my_customers_at_risk: { one: "de mes clients est à risque", many: "de mes clients sont à risque", to: "/clients?health=at_risk&manager=me" },
  my_reviews_due: { one: "revue client à faire", many: "revues client à faire", to: "/clients?review=due&manager=me" },
  my_tickets_breached: { one: "de mes tickets a dépassé son délai", many: "de mes tickets ont dépassé leur délai", to: "/support/tickets?responsible=me&delay=breached" },
  tickets_unassigned: { one: "ticket sans responsable", many: "tickets sans responsable", to: "/support/tickets?responsible=none&state=open" },
  tickets_to_attach: { one: "demande de Jamora à rattacher à sa pharmacie", many: "demandes de Jamora à rattacher à leur pharmacie", to: "/support/tickets?to_attach=1" },
  invoices_to_record: { one: "échéance à facturer", many: "échéances à facturer", to: "/revenus/factures" },
  billing_overdue: { one: "échéance impayée en retard", many: "échéances impayées en retard", to: "/revenus/factures?tab=overdue" },
  trials_ended: { one: "essai terminé à facturer ou arrêter", many: "essais terminés à facturer ou arrêter", to: "/revenus/abonnements?trial_ended=1" },
  automation_failed: { one: "automatisation à examiner", many: "automatisations à examiner", to: "/settings/automation" },
  records_to_approve: { one: "document à approuver", many: "documents à approuver", to: "/documents?view=review" },
  obligations_due: { one: "de mes obligations arrive à échéance", many: "de mes obligations arrivent à échéance", to: "/documents/obligations?vue=mine" },
  product_to_triage: { one: "retour produit à qualifier", many: "retours produit à qualifier", to: "/produit?status=proposed" },
  incident_postmortem_due: { one: "post-mortem d'incident en retard", many: "post-mortems d'incident en retard", to: "/produit/incidents?state=resolved" },
  requests_to_approve: { one: "demande d'achat à approuver", many: "demandes d'achat à approuver", to: "/achats/demandes?scope=to_approve" },
  requests_to_order: { one: "demande d'achat approuvée à commander", many: "demandes d'achat approuvées à commander", to: "/achats/demandes?scope=to_order" },
  asset_deadlines: { one: "échéance d'actif à traiter (prêt, garantie, renouvellement)", many: "échéances d'actifs à traiter (prêt, garantie, renouvellement)", to: "/achats/actifs" },
  decisions_to_adopt: { one: "décision attend votre arbitrage", many: "décisions attendent votre arbitrage", to: "/gouvernance/decisions?view=to_adopt" },
  deferred_decisions_due: { one: "décision différée à revoir", many: "décisions différées à revoir", to: "/gouvernance/decisions?view=undecided" },
  risks_review_due: { one: "de vos risques est à revoir", many: "de vos risques sont à revoir", to: "/gouvernance/risques?review=1&mine=1" },
  knowledge_review_due: { one: "de vos articles est à relire", many: "de vos articles sont à relire", to: "/support/connaissances?view=to_review" },
  campaigns_to_close: { one: "campagne dont la date de fin est passée à terminer", many: "campagnes dont la date de fin est passée à terminer", to: "/marketing/campagnes" },
  partner_agreements_due: { one: "convention de partenaire arrive à échéance ou a pris fin", many: "conventions de partenaires arrivent à échéance ou ont pris fin", to: "/marketing/partenaires" },
  my_sessions_to_close: {
    one: "de mes sessions de formation à clôturer", many: "de mes sessions de formation à clôturer", to: "/formations", state: { sessions: "mine" },
  },
};

export function plural(count, one, many) {
  return `${count} ${count > 1 ? many : one}`;
}

// The lines worth showing, in the server's order; unknown ids (a newer server) are skipped rather than shown raw.
export function todoLines(data) {
  return (data?.lines || [])
    .filter((line) => line.count > 0 && TODO_LINES[line.id])
    .map((line) => ({ ...TODO_LINES[line.id], id: line.id, count: line.count, text: plural(line.count, TODO_LINES[line.id].one, TODO_LINES[line.id].many) }));
}

// Filters a list may receive from the dashboard (Tasks.jsx), checked against the values that list knows — never applied blindly.
const TASK_FILTER_VALUES = {
  due: ["today", "this_week", "overdue"],
  priority: ["critical"],
  type: ["rdv"],
  responsable: ["moi"],
};

export function taskFiltersFromState(state) {
  const wanted = state?.filters;
  if (!wanted || typeof wanted !== "object") return null;
  const out = {};
  Object.entries(TASK_FILTER_VALUES).forEach(([key, allowed]) => {
    if (allowed.includes(wanted[key])) out[key] = wanted[key];
  });
  return Object.keys(out).length ? out : null;
}

// Pilots.jsx: « Mes pilotes » and one health state, nothing else.
const PILOT_HEALTH_VALUES = ["blocked", "attention", "unknown", "on_track"];
export function pilotFiltersFromState(state) {
  const wanted = state?.filters;
  if (!wanted || typeof wanted !== "object") return null;
  const out = {};
  if (wanted.responsible === "me") out.responsible = "me";
  if (PILOT_HEALTH_VALUES.includes(wanted.health)) out.health = wanted.health;
  return Object.keys(out).length ? out : null;
}

export function formatPercent(value) {
  return `${String(value ?? 0).replace(".", ",")} %`;
}

export function sortedEntries(map) {
  return Object.entries(map || {}).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], "fr"));
}

export function greeting(name, now = new Date()) {
  const first = (name || "").trim().split(/\s+/)[0];
  const hello = now.getHours() < 18 ? "Bonjour" : "Bonsoir";
  return first ? `${hello} ${first}` : hello;
}

export function todayLabel(now = new Date()) {
  const text = now.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
  return text.charAt(0).toUpperCase() + text.slice(1);
}

// A refresh when the tab comes back into view, at most once a minute (like the rights refresh): never a background poll.
export const REFRESH_ON_RETURN_MS = 60000;
export function shouldRefreshOnReturn(lastLoadedAt, now = Date.now()) {
  return !lastLoadedAt || now - lastLoadedAt >= REFRESH_ON_RETURN_MS;
}
